import { Sim } from "../sim/sim";
import { Input } from "./input";
import { Camera } from "../render/camera";
import { getDef } from "../data";
import { drawSprite, paletteFor } from "../render/art";
import { OWNER_PLAYER } from "../sim/constants";
import { audio } from "../render/audio";
import { toggleSettings } from "./settingsui";

// DOM overlay HUD (GDD §8.1). Unified RTS chrome: one top strip (resources +
// clock/wind + system buttons) and one bottom command bar with three zones —
// LEFT selection panel, CENTER command card (clickable order + build buttons
// mirroring the hotkeys), RIGHT docked tactical minimap. Plus a toggleable
// controls overlay and a fading first-run hint.
//
// WHY DOM (not canvas): buttons, hover tooltips, focus/enable states and
// keyboard a11y are far cleaner as DOM, and it keeps the game canvas a pure
// procedural render surface (no HUD compositing cost in the 60fps hot loop).
// Every panel is `pointer-events:auto` so HUD clicks are captured by the HUD and
// never fall through to the world — drag-select can only begin on the canvas.

const HOTKEYS = "QWERASDF"; // build-menu hotkey hints (first N options)

const CSS = `
#hud { position:fixed; inset:0; pointer-events:none; z-index:10;
  font-family:"Segoe UI","Helvetica Neue",Arial,sans-serif; color:#dfe8ef;
  --panel:#0b1219; --panel2:#0e1720; --line:#1e2c37; --line2:#2a3d4a;
  --cyan:#40e0ff; --amber:#ffb84d; --red:#ff5a3c; --green:#7ce6a0; --dim:#7f96a8;
  --mono:ui-monospace,"SFMono-Regular",Menlo,Consolas,"Liberation Mono",monospace; }
#hud .panel { pointer-events:auto; }
.mono { font-family:var(--mono); }

/* ============ TOP STRIP ============ */
#topbar { position:absolute; top:10px; left:10px; right:10px; display:flex; gap:10px; align-items:stretch;
  justify-content:space-between; }
#topleft { display:flex; gap:8px; align-items:stretch; }
#topright { display:flex; gap:8px; align-items:stretch; }
.res { position:relative; width:230px; padding:7px 12px 8px; border-radius:8px;
  background:linear-gradient(180deg,#0e1720f2,#0a1017e6); border:1px solid var(--line);
  box-shadow:inset 0 1px 0 #ffffff08; overflow:hidden; }
.res::before { content:""; position:absolute; left:0; top:0; bottom:0; width:3px; }
.res.metal::before { background:linear-gradient(#e7edf2,#9fb0bd); }
.res.energy::before { background:linear-gradient(#ffe58a,#f2c14e); }
.res .top { display:flex; align-items:baseline; justify-content:space-between; margin-bottom:3px; }
.res .name { font-family:var(--mono); font-size:10px; letter-spacing:3px; color:var(--dim); display:flex; align-items:center; gap:6px; }
.res .name .dot { width:7px; height:7px; border-radius:50%; }
.res.metal .name .dot { background:#cfd8df; }
.res.energy .name .dot { background:#f2c14e; }
.res .cur { font-family:var(--mono); font-size:19px; font-weight:600; font-variant-numeric:tabular-nums; letter-spacing:.5px; line-height:1; }
.res .capinline { font-size:11px; font-weight:400; color:var(--dim); }
.res .bar { position:relative; height:6px; margin:6px 0 5px; background:#060b10; border-radius:4px;
  border:1px solid #1a2832; overflow:hidden; }
.res .fill { position:absolute; inset:0; width:0%; border-radius:3px; transition:width .12s linear; }
.res.metal .fill { background:linear-gradient(90deg,#aebac4,#eef3f7); }
.res.energy .fill { background:linear-gradient(90deg,#e0af3e,#ffe58a); }
.res .foot { display:flex; justify-content:space-between; font-family:var(--mono); font-size:11px; font-variant-numeric:tabular-nums; }
.res .delta { font-weight:600; }
.res .delta.pos { color:var(--green); } .res .delta.neg { color:var(--red); }
.res .warn { position:absolute; top:6px; right:10px; font-family:var(--mono); font-size:10px; letter-spacing:1px; font-weight:700;
  opacity:0; transition:.15s; }
.res.stall .warn.s { opacity:1; color:var(--red); } .res.over .warn.o { opacity:1; color:var(--amber); }
.res.stall { border-color:#5a2a24; animation:pulseRed 0.7s infinite; }
.res.over { border-color:#5a4620; animation:pulseAmber 0.9s infinite; }
@keyframes pulseRed { 0%,100%{box-shadow:inset 0 1px 0 #ffffff08, 0 0 0 0 #ff5a3c00} 50%{box-shadow:inset 0 1px 0 #ffffff08, 0 0 14px 1px #ff5a3c66} }
@keyframes pulseAmber { 0%,100%{box-shadow:inset 0 1px 0 #ffffff08, 0 0 0 0 #ffb84d00} 50%{box-shadow:inset 0 1px 0 #ffffff08, 0 0 14px 1px #ffb84d55} }

#statusbox { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:0 14px;
  background:linear-gradient(180deg,#0e1720f2,#0a1017e6); border:1px solid var(--line); border-radius:8px; }
#clock { font-family:var(--mono); font-variant-numeric:tabular-nums; font-size:16px; letter-spacing:1px; color:#cdd9e2; }
#wind { font-family:var(--mono); font-size:9px; color:var(--dim); letter-spacing:1.5px; margin-top:2px; }
.iconbtn { pointer-events:auto; width:40px; display:flex; align-items:center; justify-content:center;
  background:linear-gradient(180deg,#0e1720f2,#0a1017e6); border:1px solid var(--line); border-radius:8px; cursor:pointer; color:#c5d2dc; transition:.12s; }
.iconbtn:hover { border-color:var(--cyan); color:#e8f4ff; }
.iconbtn.on { border-color:var(--cyan); color:#e8f4ff; box-shadow:0 0 12px -4px var(--cyan); }
.iconbtn svg { width:18px; height:18px; }

/* ============ BOTTOM COMMAND BAR ============ */
#bottombar { position:absolute; left:10px; right:10px; bottom:10px; height:168px; display:flex; gap:10px; align-items:stretch;
  padding:8px; border-radius:10px; background:linear-gradient(180deg,#0c1219f2,#080d12f7);
  border:1px solid var(--line2); box-shadow:0 12px 34px -14px #000d, inset 0 1px 0 #ffffff06; }
.zone { position:relative; }
.zonelabel { position:absolute; top:-1px; left:8px; font-family:var(--mono); font-size:8px; letter-spacing:2px; color:#5f7688; }

/* LEFT: selection */
#selzone { flex:0 0 300px; padding:14px 6px 6px; overflow:hidden; border-right:1px solid #16222c; }
#selempty { color:#4c6272; font-size:11px; letter-spacing:.5px; padding:18px 8px; font-family:var(--mono); }
#selgrid { display:flex; flex-wrap:wrap; gap:6px; align-content:flex-start; max-height:120px; overflow-y:auto; }
.selcard { position:relative; width:52px; background:#0e1720e6; border:1px solid var(--line); border-radius:7px;
  padding:3px 3px 4px; text-align:center; cursor:pointer; transition:.1s; }
.selcard:hover { border-color:var(--cyan); }
.selcard canvas { display:block; margin:0 auto; }
.selcard .n { position:absolute; top:1px; right:3px; font-family:var(--mono); font-size:11px; font-weight:700; color:var(--cyan);
  font-variant-numeric:tabular-nums; text-shadow:0 0 4px #000; }
.selcard .hpbar { height:3px; margin:2px 2px 0; background:#08110a; border-radius:2px; overflow:hidden; }
.selcard .hpfill { height:100%; width:100%; }
.selcard .nm { font-size:8px; letter-spacing:.3px; color:#aebcc7; margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
#selmeta { margin-top:6px; font-family:var(--mono); font-size:11px; color:var(--dim); font-variant-numeric:tabular-nums; letter-spacing:.5px; }

/* CENTER: command card */
#cmdzone { flex:1 1 auto; padding:14px 8px 6px; display:flex; flex-direction:column; gap:8px; min-width:0; }
#cmdrow { display:flex; gap:6px; }
.cmdbtn { pointer-events:auto; position:relative; width:60px; height:52px; border-radius:8px; cursor:pointer;
  background:linear-gradient(180deg,#0f1a24,#0b131b); border:1px solid var(--line); color:#c5d2dc;
  display:flex; flex-direction:column; align-items:center; justify-content:center; gap:3px; transition:.1s; }
.cmdbtn:hover:not(:disabled) { border-color:var(--cyan); color:#e8f4ff; transform:translateY(-1px); }
.cmdbtn:disabled { opacity:.32; cursor:default; }
.cmdbtn.active { border-color:var(--amber); color:#ffe1a8; box-shadow:0 0 12px -3px var(--amber); }
.cmdbtn.danger:hover:not(:disabled) { border-color:var(--red); color:#ffd0c6; }
.cmdbtn svg { width:20px; height:20px; }
.cmdbtn .lbl { font-family:var(--mono); font-size:8px; letter-spacing:1px; }
.cmdbtn .hk { position:absolute; top:2px; right:4px; font-family:var(--mono); font-size:8px; color:#5f7688; font-weight:700; }
#buildmenu { display:grid; grid-template-columns:repeat(auto-fill,54px); gap:5px; align-content:flex-start; overflow-y:auto; max-height:72px; }
.btn { pointer-events:auto; position:relative; width:54px; height:54px; border-radius:7px; cursor:pointer;
  background:linear-gradient(180deg,#0f1a24,#0b131b); border:1px solid var(--line);
  display:flex; flex-direction:column; align-items:center; transition:.1s; }
.btn:hover { border-color:var(--cyan); transform:translateY(-1px); box-shadow:0 0 0 1px #40e0ff44; }
.btn canvas { margin-top:3px; }
.btn .nm { font-size:7px; line-height:1; color:#c5d2dc; letter-spacing:.2px; }
.btn .cost { position:absolute; bottom:1px; left:3px; right:3px; display:flex; justify-content:space-between; font-family:var(--mono); font-size:8px; font-variant-numeric:tabular-nums; }
.btn .cM { color:#dbe3ea; } .btn .cE { color:#f2c14e; }
.btn .q { position:absolute; top:1px; right:4px; font-family:var(--mono); font-size:11px; color:var(--cyan); font-weight:bold; text-shadow:0 0 4px #000; }
.btn .hk { position:absolute; top:1px; left:4px; font-family:var(--mono); font-size:8px; color:#5f7688; font-weight:700; }
.tooltip { position:fixed; z-index:60; pointer-events:none; max-width:220px; background:#0c1219f5; border:1px solid var(--line2);
  border-radius:8px; padding:9px 11px; font-size:11px; color:#c5d2dc; box-shadow:0 12px 30px -10px #000e; display:none; line-height:1.5; }
.tooltip b { color:#e8f4ff; font-size:12px; letter-spacing:.5px; }
.tooltip .tstat { color:var(--dim); }
.tooltip .tval { color:#dbe3ea; font-variant-numeric:tabular-nums; }

/* RIGHT: minimap */
#mmzone { flex:0 0 152px; padding:12px 4px 4px; display:flex; align-items:center; justify-content:center; border-left:1px solid #16222c; }
#minimap { display:block; border-radius:5px; }

/* ============ HINT + HELP ============ */
#hintbar { position:absolute; bottom:186px; left:50%; transform:translateX(-50%); font-family:var(--mono); font-size:11px;
  letter-spacing:.5px; color:#9fb0bd; background:#0c1219e0; border:1px solid var(--line); padding:6px 16px; border-radius:16px;
  opacity:0; transition:opacity .5s ease; pointer-events:none; white-space:nowrap; }
#hintbar.show { opacity:1; }
#pauseind { position:absolute; top:70px; left:50%; transform:translateX(-50%); font-family:var(--mono); font-size:12px; letter-spacing:2px;
  color:var(--amber); background:#0e1720cc; border:1px solid #5a4620; padding:6px 16px; border-radius:20px; display:none; }
#helpoverlay { position:absolute; inset:0; z-index:70; display:none; align-items:center; justify-content:center;
  background:#04060ac2; pointer-events:auto; backdrop-filter:blur(2px); }
#helpoverlay.show { display:flex; }
#helpcard { width:min(680px,86vw); background:linear-gradient(180deg,#0d1720f7,#0a1017f7); border:1px solid var(--line2);
  border-radius:12px; padding:22px 26px 24px; box-shadow:0 30px 80px -20px #000e; }
#helpcard h2 { margin:0 0 4px; font-family:var(--mono); font-size:15px; letter-spacing:4px; color:#e8f4ff; }
#helpcard .sub { color:var(--dim); font-size:11px; letter-spacing:1px; margin-bottom:16px; font-family:var(--mono); }
#helpgrid { display:grid; grid-template-columns:1fr 1fr; gap:6px 26px; }
#helpgrid .row { display:flex; justify-content:space-between; gap:12px; padding:4px 0; border-bottom:1px solid #131e27; }
#helpgrid .k { color:#aebcc7; font-size:12px; }
#helpgrid .v { color:#e8f4ff; font-family:var(--mono); font-size:11px; letter-spacing:.5px; text-align:right; }
#helpclose { margin-top:18px; text-align:center; color:var(--dim); font-size:10px; letter-spacing:2px; font-family:var(--mono); }
`;

// Command-card buttons (mouse equivalents of the keyboard orders).
const CMD_ICONS: Record<string, string> = {
  move: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18M3 12h18M12 3l-3 3M12 3l3 3M12 21l-3-3M12 21l3-3M3 12l3-3M3 12l3 3M21 12l-3-3M21 12l-3 3"/></svg>`,
  attack: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="7"/><path d="M12 1v4M12 19v4M1 12h4M19 12h4"/></svg>`,
  stop: `<svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>`,
  repair: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M14.5 5.5a3.5 3.5 0 0 0-4.9 4.4L4 15.5 8.5 20l5.6-5.6a3.5 3.5 0 0 0 4.4-4.9l-2.5 2.5-2-2 2.5-2.5z"/></svg>`,
  destroy: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>`,
};
interface CmdDef { id: "move" | "attack" | "stop" | "repair" | "destroy"; lbl: string; hk: string; danger?: boolean }
const CMD_DEFS: CmdDef[] = [
  { id: "move", lbl: "MOVE", hk: "RMB" },
  { id: "attack", lbl: "ATTACK", hk: "A" },
  { id: "stop", lbl: "STOP", hk: "S" },
  { id: "repair", lbl: "REPAIR", hk: "RMB" },
  { id: "destroy", lbl: "DESTRUCT", hk: "DEL", danger: true },
];

const HELP_ROWS: [string, string][] = [
  ["Select", "Left-click / drag"],
  ["Add to selection", "Shift + click"],
  ["Select all of type", "Double-click"],
  ["Sub-select a type", "Click a selection card"],
  ["Order (context)", "Right-click"],
  ["Attack-move", "A, then click"],
  ["Stop", "S"],
  ["Queue orders", "Hold Shift"],
  ["Repair / resume", "R-click own damaged site"],
  ["Self-destruct", "Del"],
  ["Control groups", "Ctrl+0..9 / 0..9"],
  ["Build", "Builder → command card → place"],
  ["Free placement", "Hold Alt (no snap)"],
  ["Zoom", "Mouse wheel"],
  ["Pan", "Arrows / edge / mid-drag / minimap"],
  ["Pause", "Space"],
  ["This help", "? or F1"],
];

export class Hud {
  root: HTMLDivElement;
  private sim: Sim;
  private input: Input;
  private cam: Camera;
  private mm: HTMLCanvasElement;
  private mmCtx: CanvasRenderingContext2D;
  private mmScale: number;
  private buildmenu: HTMLDivElement;
  private selgrid: HTMLDivElement;
  private selempty: HTMLElement;
  private selmeta: HTMLElement;
  private cmdrow: HTMLDivElement;
  private cmdBtns: Record<string, HTMLButtonElement> = {};
  private pauseInd: HTMLDivElement;
  private tooltip: HTMLDivElement;
  private hintbar: HTMLDivElement;
  private helpOverlay: HTMLDivElement;
  private helpBtn!: HTMLElement;
  private menuKey = "";
  private selKey = "";
  private pings: { x: number; y: number; age: number; color: string }[] = [];
  private els: Record<string, HTMLElement> = {};
  private dispMetal = 0;
  private dispEnergy = 0;
  private hintDismissed = false;
  private hintTimer = 0;
  private onKey: (e: KeyboardEvent) => void;

  constructor(sim: Sim, input: Input, cam: Camera) {
    this.sim = sim;
    this.input = input;
    this.cam = cam;
    if (!document.getElementById("hud-style")) {
      const st = document.createElement("style");
      st.id = "hud-style";
      st.textContent = CSS;
      document.head.appendChild(st);
    }
    this.root = document.createElement("div");
    this.root.id = "hud";
    this.root.innerHTML = `
      <div id="topbar">
        <div id="topleft">
          <div class="res metal panel" id="mcard">
            <div class="warn s">⚠ STALL</div><div class="warn o">▲ OVERFLOW</div>
            <div class="top"><span class="name"><span class="dot"></span>METAL</span></div>
            <div class="cur"><span id="mcur">0</span> <span class="capinline" id="mcap"></span></div>
            <div class="bar"><div class="fill" id="mfill"></div></div>
            <div class="foot"><span class="delta" id="mflow">+0/s</span><span class="cap" id="mio"></span></div>
          </div>
          <div class="res energy panel" id="ecard">
            <div class="warn s">⚠ STALL</div><div class="warn o">▲ OVERFLOW</div>
            <div class="top"><span class="name"><span class="dot"></span>ENERGY</span></div>
            <div class="cur"><span id="ecur">0</span> <span class="capinline" id="ecap"></span></div>
            <div class="bar"><div class="fill" id="efill"></div></div>
            <div class="foot"><span class="delta" id="eflow">+0/s</span><span class="cap" id="eio"></span></div>
          </div>
        </div>
        <div id="topright">
          <div id="statusbox" class="panel"><div id="clock"><span id="gtime">0:00</span></div><div id="wind"></div></div>
          <div class="iconbtn panel" id="pausebtn" title="Pause (Space)">
            <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
          </div>
          <div class="iconbtn panel" id="helpbtn" title="Controls (?)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9.1 9a3 3 0 1 1 4.5 2.6c-.9.5-1.6 1.2-1.6 2.4"/><circle cx="12" cy="17.5" r="0.6" fill="currentColor" stroke="none"/></svg>
          </div>
          <div class="iconbtn panel" id="setbtn" title="Settings">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1"/></svg>
          </div>
        </div>
      </div>
      <div id="pauseind">PAUSED — SPACE TO RESUME</div>
      <div id="bottombar" class="panel">
        <div id="selzone" class="zone">
          <div class="zonelabel">SELECTION</div>
          <div id="selempty">No units selected</div>
          <div id="selgrid"></div>
          <div id="selmeta"></div>
        </div>
        <div id="cmdzone" class="zone">
          <div class="zonelabel">COMMANDS</div>
          <div id="cmdrow"></div>
          <div id="buildmenu"></div>
        </div>
        <div id="mmzone" class="zone">
          <div class="zonelabel">TACTICAL</div>
          <canvas id="minimap" width="148" height="148"></canvas>
        </div>
      </div>
      <div id="hintbar"></div>
      <div id="helpoverlay">
        <div id="helpcard">
          <h2>CONTROLS</h2>
          <div class="sub">TORRENT — command reference</div>
          <div id="helpgrid"></div>
          <div id="helpclose">? / F1 / ESC to close</div>
        </div>
      </div>
      <div class="tooltip" id="tooltip"></div>
    `;
    document.getElementById("app")!.appendChild(this.root);

    for (const id of ["mfill", "mcur", "mcap", "mflow", "mio", "efill", "ecur", "ecap", "eflow", "eio", "wind", "gtime", "mcard", "ecard"]) {
      this.els[id] = this.root.querySelector("#" + id) as HTMLElement;
    }
    this.buildmenu = this.root.querySelector("#buildmenu") as HTMLDivElement;
    this.selgrid = this.root.querySelector("#selgrid") as HTMLDivElement;
    this.selempty = this.root.querySelector("#selempty") as HTMLElement;
    this.selmeta = this.root.querySelector("#selmeta") as HTMLElement;
    this.cmdrow = this.root.querySelector("#cmdrow") as HTMLDivElement;
    this.pauseInd = this.root.querySelector("#pauseind") as HTMLDivElement;
    this.tooltip = this.root.querySelector("#tooltip") as HTMLDivElement;
    this.hintbar = this.root.querySelector("#hintbar") as HTMLDivElement;
    this.helpOverlay = this.root.querySelector("#helpoverlay") as HTMLDivElement;
    this.helpBtn = this.root.querySelector("#helpbtn") as HTMLElement;
    this.mm = this.root.querySelector("#minimap") as HTMLCanvasElement;
    this.mmCtx = this.mm.getContext("2d")!;
    this.mmScale = this.mm.width / Math.max(sim.world.gmap.worldW, sim.world.gmap.worldH);

    (this.root.querySelector("#pausebtn") as HTMLElement).onclick = () => {
      this.input.paused = !this.input.paused;
      audio.ui("click");
    };
    (this.root.querySelector("#setbtn") as HTMLElement).onclick = () => toggleSettings(12, 60);
    this.helpBtn.onclick = () => this.toggleHelp();
    (this.helpOverlay).onclick = (e) => { if (e.target === this.helpOverlay) this.toggleHelp(false); };

    this.buildCmdRow();
    this.buildHelpGrid();
    this.bindMinimap();

    // Global keys for the HUD-owned overlay (kept out of the sim's Input path).
    this.onKey = (e: KeyboardEvent) => {
      if (e.key === "F1" || e.key === "?") { e.preventDefault(); this.toggleHelp(); }
      else if (e.key === "Escape" && this.helpOverlay.classList.contains("show")) this.toggleHelp(false);
    };
    window.addEventListener("keydown", this.onKey);

    const eco = sim.world.eco[OWNER_PLAYER];
    this.dispMetal = eco.metal;
    this.dispEnergy = eco.energy;

    // First-run hint (fades after the player selects, or after a while).
    this.showHint("Drag to select · right-click to command · press ? for controls");
  }

  destroy(): void {
    window.removeEventListener("keydown", this.onKey);
    this.root.remove();
  }

  addPing(x: number, y: number, color: string): void {
    this.pings.push({ x, y, age: 0, color });
    if (this.pings.length > 40) this.pings.shift();
  }

  // ---- Command card ----
  private buildCmdRow(): void {
    for (const cd of CMD_DEFS) {
      const b = document.createElement("button");
      b.className = "cmdbtn" + (cd.danger ? " danger" : "");
      b.dataset.cmd = cd.id;
      b.innerHTML = `<span class="hk">${cd.hk}</span>${CMD_ICONS[cd.id]}<span class="lbl">${cd.lbl}</span>`;
      b.onclick = () => this.runCmd(cd.id);
      this.cmdrow.appendChild(b);
      this.cmdBtns[cd.id] = b;
    }
  }

  private runCmd(id: string): void {
    switch (id) {
      case "move": this.input.armMove(); break;
      case "attack": this.input.armAttackMove(); break;
      case "stop": this.input.stopSelected(); break;
      case "repair": this.input.armRepair(); break;
      case "destroy": this.input.selfDestructSelected(); break;
    }
    audio.ui("click");
  }

  private updateCmdRow(): void {
    const mobile = this.input.hasMobileSelected();
    const builder = this.input.hasBuilderSelected();
    const own = this.input.hasOwnSelected();
    const en: Record<string, boolean> = { move: mobile, attack: mobile, stop: mobile, repair: builder, destroy: own };
    const armed = this.input.armed;
    const armMap: Record<string, string> = { move: "move", attack: "attack", repair: "repair" };
    for (const cd of CMD_DEFS) {
      const b = this.cmdBtns[cd.id];
      b.disabled = !en[cd.id];
      b.classList.toggle("active", armed !== "" && armMap[cd.id] === armed);
    }
  }

  // ---- Help overlay + hint ----
  private buildHelpGrid(): void {
    const g = this.root.querySelector("#helpgrid") as HTMLElement;
    g.innerHTML = HELP_ROWS.map(([k, v]) => `<div class="row"><span class="k">${k}</span><span class="v">${v}</span></div>`).join("");
  }
  private toggleHelp(force?: boolean): void {
    const show = force ?? !this.helpOverlay.classList.contains("show");
    this.helpOverlay.classList.toggle("show", show);
    this.helpBtn.classList.toggle("on", show);
    audio.ui("click");
  }
  private showHint(text: string): void {
    this.hintbar.textContent = text;
    this.hintbar.classList.add("show");
    this.hintTimer = 0;
  }
  private dismissHint(): void {
    if (this.hintDismissed) return;
    this.hintDismissed = true;
    this.hintbar.classList.remove("show");
  }

  private bindMinimap(): void {
    const toWorld = (e: MouseEvent) => {
      const r = this.mm.getBoundingClientRect();
      const wx = ((e.clientX - r.left) / r.width) * this.sim.world.gmap.worldW;
      const wy = ((e.clientY - r.top) / r.height) * this.sim.world.gmap.worldH;
      return { wx, wy };
    };
    this.mm.addEventListener("mousedown", (e) => {
      e.preventDefault();
      const { wx, wy } = toWorld(e);
      if (e.button === 0) this.cam.centerOn(wx, wy);
    });
    this.mm.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  update(dt: number): void {
    const w = this.sim.world;
    const eco = w.eco[OWNER_PLAYER];

    // Eased counters (smooth readout).
    const k = 1 - Math.pow(0.001, dt);
    this.dispMetal += (eco.metal - this.dispMetal) * k;
    this.dispEnergy += (eco.energy - this.dispEnergy) * k;

    (this.els.mfill as HTMLElement).style.width = `${Math.min(100, (eco.metal / Math.max(1, eco.metalCap)) * 100)}%`;
    (this.els.efill as HTMLElement).style.width = `${Math.min(100, (eco.energy / Math.max(1, eco.energyCap)) * 100)}%`;
    this.els.mcur.textContent = Math.round(this.dispMetal).toString();
    this.els.ecur.textContent = Math.round(this.dispEnergy).toString();
    this.els.mcap.textContent = `/ ${eco.metalCap.toFixed(0)}`;
    this.els.ecap.textContent = `/ ${eco.energyCap.toFixed(0)}`;

    this.setDelta(this.els.mflow, eco.metalIncome - eco.metalExpense);
    this.setDelta(this.els.eflow, eco.energyIncome - eco.energyExpense);
    this.els.mio.textContent = `+${eco.metalIncome.toFixed(0)} −${eco.metalExpense.toFixed(0)}`;
    this.els.eio.textContent = `+${eco.energyIncome.toFixed(0)} −${eco.energyExpense.toFixed(0)}`;
    this.els.wind.textContent = `WIND ${w.windCurrent.toFixed(0)}`;

    this.setState(this.els.mcard, eco.metalWasted > 0, eco.stalled && eco.metal < eco.metalCap * 0.05);
    this.setState(this.els.ecard, eco.energyWasted > 0, eco.stalled && eco.energy < eco.energyCap * 0.05);

    this.els.gtime.textContent = fmtTime(w.tick / 30);
    this.pauseInd.style.display = this.input.paused ? "block" : "none";

    this.updateBuildMenu();
    this.updateSelPanel();
    this.updateCmdRow();

    // Fade the first-run hint once the player selects, or after a while.
    if (!this.hintDismissed) {
      this.hintTimer += dt;
      if (this.input.selection.size > 0 || this.hintTimer > 14) this.dismissHint();
    }

    for (let i = this.pings.length - 1; i >= 0; i--) {
      this.pings[i].age += dt;
      if (this.pings[i].age > 2) this.pings.splice(i, 1);
    }
    this.drawMinimap();
  }

  private setDelta(el: HTMLElement, v: number): void {
    el.textContent = `${v >= 0 ? "▲ +" : "▼ "}${v.toFixed(0)}/s`;
    el.classList.toggle("pos", v >= 0);
    el.classList.toggle("neg", v < 0);
  }

  private setState(card: HTMLElement, over: boolean, stall: boolean): void {
    card.classList.toggle("over", over && !stall);
    card.classList.toggle("stall", stall);
  }

  private menuMode(): { mode: "build" | "factory" | "none"; defId: string; builderDef: string } {
    const w = this.sim.world;
    let factory = -1;
    let builder = -1;
    for (const e of this.input.selection) {
      if (!w.isAlive(e)) continue;
      const d = w.def(e);
      if (d.role === "factory" && factory < 0) factory = e;
      else if (d.buildPower > 0 && d.buildOptions && builder < 0) builder = e;
    }
    if (factory >= 0) return { mode: "factory", defId: "", builderDef: w.def(factory).id };
    if (builder >= 0) return { mode: "build", defId: "", builderDef: w.def(builder).id };
    return { mode: "none", defId: "", builderDef: "" };
  }

  private updateBuildMenu(): void {
    const m = this.menuMode();
    const key = m.mode + ":" + m.builderDef;
    if (key !== this.menuKey) {
      this.menuKey = key;
      this.rebuildMenu(m.mode, m.builderDef);
    }
    if (m.mode === "factory") {
      const w = this.sim.world;
      const counts: Record<string, number> = {};
      for (const e of this.input.selection) {
        if (w.isAlive(e) && w.def(e).role === "factory") {
          for (const id of w.factoryQueue[e]) counts[id] = (counts[id] ?? 0) + 1;
        }
      }
      for (const btn of Array.from(this.buildmenu.children) as HTMLElement[]) {
        const id = btn.dataset.id!;
        const q = counts[id] ?? 0;
        (btn.querySelector(".q") as HTMLElement).textContent = q > 0 ? String(q) : "";
      }
    }
  }

  private rebuildMenu(mode: "build" | "factory" | "none", builderDef: string): void {
    this.buildmenu.innerHTML = "";
    this.tooltip.style.display = "none";
    if (mode === "none" || !builderDef) return;
    const def = getDef(builderDef);
    const opts = def.buildOptions ?? [];
    opts.forEach((id, idx) => {
      const od = getDef(id);
      const btn = document.createElement("div");
      btn.className = "btn";
      btn.dataset.id = id;
      const cv = document.createElement("canvas");
      cv.width = 38; cv.height = 30;
      const g = cv.getContext("2d")!;
      g.translate(19, 16);
      const scale = Math.min(1.4, 12 / od.radius);
      g.scale(scale, scale);
      if (od.kind !== "building") g.rotate(-Math.PI / 2);
      drawSprite(g, od.role, od.faction, od.radius, paletteFor(od.faction));
      btn.appendChild(cv);
      const nm = document.createElement("div"); nm.className = "nm"; nm.textContent = od.name;
      btn.appendChild(nm);
      const cost = document.createElement("div"); cost.className = "cost";
      cost.innerHTML = `<span class="cM">${od.metalCost}</span><span class="cE">${od.energyCost}</span>`;
      btn.appendChild(cost);
      const q = document.createElement("div"); q.className = "q"; btn.appendChild(q);
      if (idx < HOTKEYS.length) {
        const hk = document.createElement("div"); hk.className = "hk"; hk.textContent = HOTKEYS[idx]; btn.appendChild(hk);
      }
      btn.onclick = (ev) => {
        audio.ui("click");
        if (mode === "build") this.input.startPlacing(id);
        else this.input.queueUnit(id, ev.shiftKey ? 5 : 1);
      };
      btn.oncontextmenu = (ev) => {
        ev.preventDefault();
        if (mode === "factory") { this.input.cancelUnit(id); audio.ui("deny"); }
      };
      btn.onmouseenter = () => this.showTooltip(btn, od, mode);
      btn.onmouseleave = () => { this.tooltip.style.display = "none"; };
      this.buildmenu.appendChild(btn);
    });
  }

  private showTooltip(btn: HTMLElement, od: ReturnType<typeof getDef>, mode: string): void {
    const r = btn.getBoundingClientRect();
    const stats: string[] = [
      `<div><span class="tstat">Cost</span> <span class="tval">${od.metalCost} M · ${od.energyCost} E</span></div>`,
      `<div><span class="tstat">Build</span> <span class="tval">${od.buildTime}s</span> · <span class="tstat">HP</span> <span class="tval">${od.maxHealth}</span></div>`,
    ];
    if (od.weapon) stats.push(`<div><span class="tstat">Dmg</span> <span class="tval">${od.weapon.damage}</span> · <span class="tstat">Rng</span> <span class="tval">${od.weapon.range}</span>${od.weapon.aoeRadius ? ` · <span class="tstat">AoE</span> <span class="tval">${od.weapon.aoeRadius}</span>` : ""}</div>`);
    if (od.econ?.metalYield) stats.push(`<div><span class="tstat">Metal</span> <span class="tval">+${od.econ.metalYield}/s</span></div>`);
    if (od.econ?.energyYield) stats.push(`<div><span class="tstat">Energy</span> <span class="tval">+${od.econ.energyYield}/s</span></div>`);
    if (od.econ?.windTurbine) stats.push(`<div><span class="tstat">Energy</span> <span class="tval">wind-based</span></div>`);
    const hint = mode === "factory" ? `<div class="tstat" style="margin-top:5px">Click: queue · Shift: ×5 · R-click: cancel</div>`
      : `<div class="tstat" style="margin-top:5px">Click, then place</div>`;
    this.tooltip.innerHTML = `<b>${od.name}</b>${stats.join("")}${hint}`;
    this.tooltip.style.display = "block";
    this.tooltip.style.left = r.left + "px";
    this.tooltip.style.top = (r.top - this.tooltip.offsetHeight - 8) + "px";
  }

  /** Vector portrait silhouette in the selection card (zero-asset, always available). */
  private drawSelSilhouette(g: CanvasRenderingContext2D, role: string, fac: string, radius: number): void {
    g.save();
    g.translate(21, 17);
    const scale = Math.min(1.2, 10 / radius);
    g.scale(scale, scale);
    if (role !== "factory" && role !== "extractor" && role !== "solar" && role !== "wind" && role !== "converter" && role !== "storage" && role !== "turret") g.rotate(-Math.PI / 2);
    drawSprite(g, role as never, fac as never, radius, paletteFor(fac as never));
    g.restore();
  }

  private updateSelPanel(): void {
    const sel = this.input.selection;
    const w = this.sim.world;
    if (sel.size === 0) {
      this.selempty.style.display = "block";
      if (this.selKey !== "") { this.selgrid.innerHTML = ""; this.selmeta.textContent = ""; this.selKey = ""; }
      return;
    }
    this.selempty.style.display = "none";

    const counts: Record<string, { n: number; def: string; hp: number; maxHp: number; role: string; fac: string; radius: number }> = {};
    let hp = 0, maxHp = 0;
    for (const e of sel) {
      if (!w.isAlive(e)) continue;
      const d = w.def(e);
      const c = counts[d.id] ?? (counts[d.id] = { n: 0, def: d.name, hp: 0, maxHp: 0, role: d.role, fac: d.faction, radius: d.radius });
      c.n++; c.hp += w.health[e]; c.maxHp += w.maxHealth[e];
      hp += w.health[e]; maxHp += w.maxHealth[e];
    }
    const types = Object.entries(counts);
    const key = types.map(([id, c]) => id + c.n + Math.round((c.hp / Math.max(1, c.maxHp)) * 8)).join(",");
    if (key !== this.selKey) {
      this.selKey = key;
      this.selgrid.innerHTML = "";
      for (const [id, c] of types.slice(0, 12)) {
        const frac = Math.max(0, Math.min(1, c.hp / Math.max(1, c.maxHp)));
        const card = document.createElement("div");
        card.className = "selcard";
        card.title = `${c.def} ×${c.n} — click to sub-select`;
        const cv = document.createElement("canvas");
        cv.width = 42; cv.height = 34;
        const g = cv.getContext("2d")!;
        this.drawSelSilhouette(g, c.role, c.fac, c.radius);
        card.appendChild(cv);
        const n = document.createElement("div"); n.className = "n"; n.textContent = c.n > 1 ? "×" + c.n : ""; card.appendChild(n);
        const bar = document.createElement("div"); bar.className = "hpbar";
        const fill = document.createElement("div"); fill.className = "hpfill";
        fill.style.width = `${frac * 100}%`;
        fill.style.background = frac > 0.5 ? "#5bd66a" : frac > 0.25 ? "#e0c840" : "#e5533b";
        bar.appendChild(fill); card.appendChild(bar);
        const nm = document.createElement("div"); nm.className = "nm"; nm.textContent = c.def; card.appendChild(nm);
        // Click a group to sub-select only that type (unless it's the only type).
        card.onclick = () => { if (types.length > 1) { this.input.keepOnlyType(id); this.selKey = ""; } };
        this.selgrid.appendChild(card);
      }
    } else {
      // Refresh only the per-group health fills (cheap).
      const fills = this.selgrid.querySelectorAll(".hpfill");
      types.slice(0, 12).forEach(([, c], i) => {
        const frac = Math.max(0, Math.min(1, c.hp / Math.max(1, c.maxHp)));
        const el = fills[i] as HTMLElement | undefined;
        if (el) { el.style.width = `${frac * 100}%`; el.style.background = frac > 0.5 ? "#5bd66a" : frac > 0.25 ? "#e0c840" : "#e5533b"; }
      });
    }
    this.selmeta.innerHTML = `${sel.size} unit${sel.size > 1 ? "s" : ""} · HP ${hp | 0}/${maxHp | 0}`;
  }

  private drawMinimap(): void {
    const g = this.mmCtx;
    const w = this.sim.world;
    const m = w.gmap;
    const sc = this.mmScale;
    g.fillStyle = "#070d13";
    g.fillRect(0, 0, this.mm.width, this.mm.height);
    // Elevation thumbnail (top-down — the minimap stays a map, by design).
    const step = 2;
    for (let r = 0; r < m.rows; r += step) {
      for (let c = 0; c < m.cols; c += step) {
        const h = m.heights[r * m.cols + c];
        const v = 16 + h * 62;
        g.fillStyle = `rgb(${v * 0.7 | 0},${v * 0.85 | 0},${v | 0})`;
        g.fillRect(c * m.cellSize * sc, r * m.cellSize * sc, m.cellSize * step * sc + 1, m.cellSize * step * sc + 1);
      }
    }
    for (let i = 0; i < m.def.metalSpots.length; i++) {
      const s = m.def.metalSpots[i];
      g.fillStyle = w.metalSpotOccupant[i] >= 0 ? "#6a7280" : "#ffd25a";
      g.fillRect(s.x * sc - 1, s.y * sc - 1, 2, 2);
    }
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e]) continue;
      const k = w.kind[e];
      if (k > 1) continue;
      const owner = w.owner[e];
      g.fillStyle = owner === OWNER_PLAYER ? "#40e0ff" : owner === 2 ? "#ff5a3c" : "#889";
      const sz = k === 1 ? 3 : w.def(e).role === "commander" ? 4 : 2;
      g.fillRect(w.posX[e] * sc - sz / 2, w.posY[e] * sc - sz / 2, sz, sz);
    }
    // Animated pings.
    for (const p of this.pings) {
      const a = 1 - p.age / 2;
      g.strokeStyle = p.color;
      g.globalAlpha = a;
      g.lineWidth = 1.5;
      g.beginPath(); g.arc(p.x * sc, p.y * sc, (p.age * 9) % 14, 0, 7); g.stroke();
      g.globalAlpha = a * 0.5;
      g.beginPath(); g.arc(p.x * sc, p.y * sc, ((p.age * 9) % 14) * 0.5, 0, 7); g.stroke();
      g.globalAlpha = 1;
    }
    // Viewport footprint: the camera frustum's ground quad (a trapezoid under
    // the perspective tilt), drawn from the four projected ground corners.
    const corners = this.cam.viewCorners();
    g.strokeStyle = "rgba(232,244,255,0.85)";
    g.lineWidth = 1;
    g.beginPath();
    for (let i = 0; i < corners.length; i++) {
      const cx = corners[i].x * sc, cy = corners[i].y * sc;
      if (i === 0) g.moveTo(cx, cy); else g.lineTo(cx, cy);
    }
    g.closePath(); g.stroke();
  }
}

function fmtTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
